import { cn } from "@garther/ui/libs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import LogoText from "@/assets/logo-text.svg?react";
import { useAuthStore } from "@/stores/auth.store";
import { useCreditStore } from "@/stores/credit.store";
import { useQuizStore } from "@/stores/quiz.store";

interface AppLoadingProps {
  children: React.ReactNode;
}

interface LoadingStep {
  title: string;
  completedTitle: string;
  action: () => Promise<void>;
  skipIfNotAuth?: boolean;
}

export function AppLoading({ children }: AppLoadingProps) {
  const { t } = useTranslation();
  const { curUser, loggedIn, loading: authLoading, loadProfile } = useAuthStore();
  const { loadUserCredits } = useCreditStore();
  const { fetchQuizData } = useQuizStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [isReady, setIsReady] = useState(false);
  console.log("[DEBUG] AppLoading", authLoading, curUser, loggedIn);
  // Memoize the steps to prevent recreation on every render
  const steps: LoadingStep[] = useMemo(
    () => [
      {
        title: t("loading.steps.checkingAuthentication"),
        completedTitle: t("loading.steps.completeAuthentication"),
        action: () =>
          new Promise<void>((resolve) => {
            // Wait for Firebase authentication to complete
            const checkAuth = () => {
              if (!authLoading) {
                resolve();
              } else {
                setTimeout(checkAuth, 100);
              }
            };
            checkAuth();
          })
      },
      {
        title: t("loading.steps.loadingUserData"),
        completedTitle: t("loading.steps.completeUserData"),
        action: async () => {
          if (curUser.fbUser?.uid) {
            await Promise.all([
              loadProfile(),
              loadUserCredits(curUser.fbUser.uid),
              fetchQuizData(curUser.fbUser.uid)
            ]);
          }
        },
        skipIfNotAuth: true
      }
    ],
    [t, authLoading, loadProfile, loadUserCredits, fetchQuizData, curUser.fbUser?.uid]
  );

  // Memoize visible steps and progress calculation
  const { visibleSteps, progress } = useMemo(() => {
    const visibleSteps = steps.filter((step) => {
      const isAuthDependentStep = step.skipIfNotAuth;
      return !isAuthDependentStep || loggedIn;
    });

    const progress =
      visibleSteps.length > 0 ? (Math.min(currentStep, visibleSteps.length) / visibleSteps.length) * 100 : 0;

    return {
      visibleSteps,
      progress
    };
  }, [steps, loggedIn, currentStep]);

  // Memoize the step execution function
  const executeSteps = useCallback(async () => {
    for (let i = 0; i < steps.length; i++) {
      try {
        const step = steps[i];
        if (step.skipIfNotAuth && !loggedIn) {
          console.log(`[DEBUG] Skipping step ${i + 1}.${step.title} - user not authenticated`);
          continue;
        }

        await step.action();
        setCurrentStep(i + 1);
      } catch (error) {
        console.error(`[DEBUG] Step ${i + 1}.${steps[i].title} failed:`, error);
      }
    }

    setTimeout(() => setIsReady(true), 300);
  }, [steps, loggedIn]);

  useEffect(() => {
    executeSteps();
  }, [steps, executeSteps]);

  if (isReady) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background-primary e-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-background-secondary rounded-sm p-4 md:p-6 border border-border-dark">
          <div className="text-center mb-8">
            <div className="w-24 mx-auto mb-4 e-center">
              <LogoText className="h-w-full h-full w-fit" />
            </div>
            <p className="body-base text-text-secondary">{t("loading.title")}</p>
          </div>
          <div className="space-y-4">
            {visibleSteps.map((step, index) => {
              return (
                <div
                  key={index}
                  className={cn(
                    "flex items-center space-x-3 transition-all",
                    index <= currentStep ? "opacity-100" : "opacity-40"
                  )}
                >
                  <div
                    className={cn(
                      "w-6 h-6 rounded-full e-center transition-all duration-500 border",
                      index < currentStep
                        ? "bg-background-transparent text-text-secondary"
                        : index === currentStep
                          ? "bg-transparent text-text-primary border border-border-dark"
                          : "bg-background-transparent text-text-secondary"
                    )}
                  >
                    {index < currentStep ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <span className="text-xs font-bold">{index + 1}</span>
                    )}
                  </div>
                  <span
                    className={`body-base transition-all duration-500 ${
                      index <= currentStep ? "text-text-primary" : "text-text-secondary"
                    }`}
                  >
                    {index < currentStep ? step.completedTitle : step.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Overall Progress */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="body-small text-text-secondary">{t("loading.progress")}</span>
              <span className="body-small text-text-primary font-medium">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-background-highlight rounded-sm h-2">
              <div
                className="bg-background-dark-secondary h-2 rounded-sm transition-all duration-500 ease-out"
                style={{
                  width: `${progress}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
